import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Trash2, History, CreditCard } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { toast } from 'react-toastify';

const PaymentModal = ({ invoice, onClose, onSave, onRefresh, isOpen }) => {
    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges] = useState(false); // Track if anything changed to avoid unnesecary refreshes out of scope

    const totalAmount = parseFloat(invoice?.grand_total || 0);
    // currentPaid will be updated from DB after load
    const [currentPaid, setCurrentPaid] = useState(parseFloat(invoice?.amount_paid || 0));

    const handleClose = () => {
        if (hasChanges && onRefresh) {
            onRefresh();
        }
        onClose();
        setHasChanges(false);
    }

    useEffect(() => {
        if (isOpen && invoice) {
            // Fetch history
            fetchHistory();
            // Reset form
            reset({
                amount: (totalAmount - parseFloat(invoice.amount_paid || 0)).toFixed(2),
                tdsAmount: '0.00',
                paymentDate: new Date().toISOString().split('T')[0],
                paymentMode: 'Cash',
                referenceNumber: '',
                notes: ''
            });
            setCurrentPaid(parseFloat(invoice.amount_paid || 0));
            setIsSubmitting(false);
            setHasChanges(false);
        }
    }, [isOpen, invoice, reset]);

    const fetchHistory = async () => {
        if (!invoice?.id) return;
        setLoadingHistory(true);
        try {
            const { data, error } = await supabase
                .from('payments')
                .select('*')
                .eq('invoice_id', invoice.id)
                .order('payment_date', { ascending: false });

            if (error) throw error;
            const historyData = data || [];
            setPaymentHistory(historyData);
            
            // Recalculate accurately from history records to avoid sync issues
            const calculatedPaid = historyData.reduce((sum, p) => sum + parseFloat(p.amount || 0) + parseFloat(p.tds_amount || 0), 0);
            setCurrentPaid(calculatedPaid);
        } catch (error) {
            console.error('Error fetching history:', error);
            toast.error('Failed to load payment history');
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleDeletePayment = async (id) => {
        if (!window.confirm('Are you sure you want to delete this payment record?')) return;
        try {
            const { error: deleteError } = await supabase.from('payments').delete().eq('id', id);
            if (deleteError) throw deleteError;

            // Recalculate remaining total
            const { data: remainingPayments, error: historyError } = await supabase
                .from('payments')
                .select('amount, tds_amount')
                .eq('invoice_id', invoice.id);
            if (historyError) throw historyError;

            const newTotalPaid = remainingPayments.reduce((sum, p) => sum + parseFloat(p.amount) + parseFloat(p.tds_amount || 0), 0);
            const newTotalTDS = remainingPayments.reduce((sum, p) => sum + parseFloat(p.tds_amount || 0), 0);
            const grandTotal = parseFloat(invoice.grand_total || 0);

            let newStatus = 'PENDING';
            if (newTotalPaid >= grandTotal - 1 && newTotalPaid > 0) { // 1 rupee tolerance, but needs to be > 0
                newStatus = 'PAID';
            } else if (newTotalPaid > 0) {
                newStatus = 'PARTIAL';
            }

            const { error: updateError } = await supabase
                .from('invoices')
                .update({
                    amount_paid: newTotalPaid,
                    tds_amount: newTotalTDS,
                    payment_status: newStatus
                })
                .eq('id', invoice.id);

            if (updateError) throw updateError;

            toast.success('Payment deleted');
            setHasChanges(true); // Flag to refresh on close
            await fetchHistory(); // This updates setCurrentPaid locally

        } catch (error) {
            console.error('Error deleting payment:', error);
            toast.error('Failed to delete payment');
        }
    };

    const onSubmit = async (data) => {
        if (isSubmitting) return;

        const payAmount = parseFloat(data.amount || 0);
        const tdsAmount = parseFloat(data.tdsAmount || 0);
        const totalDeduction = payAmount + tdsAmount;

        // Recalculate balance to be safe, relying on currentPaid state which is updated from history
        const currentBalance = Math.max(0, totalAmount - currentPaid);

        if (totalDeduction > currentBalance + 0.5) { // 0.5 tolerance for floating point rounding
            toast.error(`Total Amount + TDS (₹${totalDeduction.toFixed(2)}) cannot exceed balance due (₹${currentBalance.toFixed(2)})`);
            return;
        }

        setIsSubmitting(true);
        try {
            // We handle save internally now to support "Add & Stay" or just simplicity
            // But keeping onSave prop for consistency if parent wants to handle it.
            // Actually, parent implementation of onSave does BOTH insert and update invoice.
            // If we want to support deleting/history here, we might need to move logic here or call onSave.

            // Let's call the parent onSave, but wait for it.
            await onSave({
                ...data,
                invoiceId: invoice.id,
                amount: payAmount,
                tdsAmount: tdsAmount
            });
            // Refresh history after save
            setHasChanges(true);
            fetchHistory();
            // Do NOT close modal immediately if we want to show it added? 
            // The parent implementation closes the modal. 
            // If we want "landscape record popup", maybe we strictly strictly want to Add.
            // But if landscape, user likely wants to see the result.
            // For now, let's stick to parent behavior: Close on success.
        } catch (error) {
            console.error("Error saving payment:", error);
            toast.error("Failed to save payment");
            setIsSubmitting(false); // Re-enable button on error
        }
    };

    if (!isOpen || !invoice) return null;

    const isPaid = invoice.payment_status === 'PAID';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            {/* Increased max-width for landscape mode */}
            <div className={`bg-white rounded-lg shadow-xl w-full ${isPaid ? 'max-w-3xl' : 'max-w-5xl'} overflow-hidden flex flex-col md:flex-row max-h-[90vh]`}>

                {/* LEFT PANEL: History & Details */}
                <div className={`w-full ${isPaid ? '' : 'md:w-1/2'} bg-gray-50 border-r border-gray-200 flex flex-col`}>
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                <History className="w-5 h-5 mr-2 text-gray-500" />
                                Payment History
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Invoice #{invoice.invoice_no} • {invoice.customers?.customer_name}
                            </p>
                        </div>
                        {isPaid && (
                            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
                                <X className="h-6 w-6" />
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto p-0">
                        {loadingHistory ? (
                            <div className="p-6 text-center text-gray-500">Loading history...</div>
                        ) : paymentHistory.length === 0 ? (
                            <div className="p-10 text-center text-gray-400 flex flex-col items-center">
                                <History className="w-12 h-12 mb-2 opacity-20" />
                                <p>No payments recorded yet</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">TDS</th>
                                        <th className="px-6 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paymentHistory.map((payment) => (
                                        <tr key={payment.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {payment.payment_date}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {payment.payment_mode}
                                                {payment.reference_number && (
                                                    <span className="block text-xs text-gray-400">{payment.reference_number}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 text-right">
                                                ₹{parseFloat(payment.amount).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600 text-right">
                                                ₹{parseFloat(payment.tds_amount || 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                    <button
                                                        onClick={() => handleDeletePayment(payment.id)}
                                                        className="text-red-400 hover:text-red-600 transition-colors"
                                                        title="Delete payment"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="p-6 bg-white border-t border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-500">Total Invoiced</span>
                            <span className="font-medium">₹{totalAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-500">Total Paid</span>
                            <span className="font-medium text-green-600">₹{currentPaid.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                            <span className="font-medium text-gray-900">Balance Due</span>
                            <span className="font-bold text-xl text-red-600">₹{parseFloat(Math.max(0, totalAmount - currentPaid)).toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL: Add Payment Form - Only if NOT paid */}
                {!isPaid && (
                    <div className="w-full md:w-1/2 flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                <CreditCard className="w-5 h-5 mr-2 text-primary-600" />
                                Record New Payment
                            </h2>
                            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5 flex-1 overflow-y-auto">

                            <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-4">
                                <p className="text-sm text-blue-800">
                                    Recording a payment will automatically update the invoice status and balance.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Amount Received (₹) *
                                    </label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">₹</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`block w-full pl-7 pr-4 py-3 border rounded-md focus:ring-primary-500 focus:border-primary-500 ${errors.amount ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="0.00"
                                            {...register('amount', {
                                                required: 'Amount is required',
                                                min: { value: 0, message: 'Amount cannot be negative' }
                                            })}
                                        />
                                    </div>
                                    {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount.message}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        TDS Amount (₹)
                                    </label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">₹</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`block w-full pl-7 pr-4 py-3 border rounded-md focus:ring-primary-500 focus:border-primary-500 ${errors.tdsAmount ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="0.00"
                                            {...register('tdsAmount', {
                                                min: { value: 0, message: 'Amount cannot be negative' }
                                            })}
                                        />
                                    </div>
                                    {errors.tdsAmount && <p className="text-red-500 text-xs mt-1">{errors.tdsAmount.message}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Date *
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                        {...register('paymentDate', { required: true })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Mode *
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                        {...register('paymentMode', { required: true })}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reference / Cheque No.
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. UPI Ref, Cheque No"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                    {...register('referenceNumber')}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Notes / Remarks
                                </label>
                                <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                    rows="3"
                                    placeholder="Optional notes..."
                                    {...register('notes')}
                                ></textarea>
                            </div>

                            <div className="pt-4 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-sm flex items-center ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? 'Processing...' : 'Record Payment'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

            </div>
        </div>
    );

};

export default PaymentModal;
